import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowUpRight, Linkedin, Cpu, Code, Layout, Instagram } from 'lucide-react';
import { motion } from 'motion/react';
import logoBlu from '../../assets/logo/Secondario-blu.svg';
import { CONTACT_INFO } from '../../data/contactInfo';

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#F4F5F8] text-gray-900 pt-16 pb-12 border-t border-[#DDDFDE]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Main Footer Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8 pb-12 border-b border-[#DDDFDE]">
          
          {/* Column 1: Brand Info (4 cols) */}
          <div className="lg:col-span-4 space-y-5">
            <Link to="/" className="inline-block">
              <img 
                src={logoBlu} 
                alt={`${CONTACT_INFO.name} — Consulenza IT & Sviluppo Software`} 
                loading="lazy"
                decoding="async"
                width="147"
                height="32"
                className="h-8 w-auto object-contain" 
              />
            </Link>
            <p className="text-gray-600 text-sm leading-relaxed max-w-sm">
              {CONTACT_INFO.tagline}
            </p>
            <div className="flex items-center gap-3 pt-2">
              <motion.a 
                whileHover={{ scale: 1.1, y: -2 }}
                whileTap={{ scale: 0.95 }}
                href={CONTACT_INFO.socials.whatsapp.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-white border border-gray-300 flex items-center justify-center text-gray-600 hover:text-[#25D366] hover:border-[#25D366] transition-colors shadow-sm"
                aria-label="WhatsApp"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </motion.a>
              <motion.a 
                whileHover={{ scale: 1.1, y: -2 }}
                whileTap={{ scale: 0.95 }}
                href={CONTACT_INFO.socials.linkedin.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-white border border-gray-300 flex items-center justify-center text-gray-600 hover:text-[#0A66C2] hover:border-[#0A66C2] transition-colors shadow-sm"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-4 h-4" />
              </motion.a>
              <motion.a 
                whileHover={{ scale: 1.1, y: -2 }}
                whileTap={{ scale: 0.95 }}
                href={`mailto:${CONTACT_INFO.email}`} 
                className="w-9 h-9 rounded-full bg-white border border-gray-300 flex items-center justify-center text-gray-600 hover:text-[#4601FA] hover:border-[#4601FA] transition-colors shadow-sm"
                aria-label="Email"
              >
                <Mail className="w-4 h-4" />
              </motion.a>
              <motion.a 
                whileHover={{ scale: 1.1, y: -2 }}
                whileTap={{ scale: 0.95 }}
                href={CONTACT_INFO.socials.instagram.url} 
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-white border border-gray-300 flex items-center justify-center text-gray-600 hover:text-[#E4405F] hover:border-[#E4405F] transition-colors shadow-sm"
                aria-label="Instagram"
              >
                <Instagram className="w-4 h-4" />
              </motion.a>
            </div>
          </div>

          {/* Column 2: Navigation Links (2 cols) */}
          <div className="lg:col-span-2 space-y-4">
            <h3 className="text-xs uppercase tracking-wider font-extrabold text-[#4601FA]">Navigazione</h3>
            <ul className="space-y-2.5 text-sm text-gray-600">
              <li>
                <Link to="/" className="hover:text-[#4601FA] transition-colors flex items-center gap-1 group">
                  <span>Home</span>
                </Link>
              </li>
              <li>
                <Link to="/about" className="hover:text-[#4601FA] transition-colors">
                  Chi Sono
                </Link>
              </li>
              <li>
                <Link to="/method" className="hover:text-[#4601FA] transition-colors">
                  Il Mio Metodo
                </Link>
              </li>
              <li>
                <Link to="/services" className="hover:text-[#4601FA] transition-colors">
                  Servizi
                </Link>
              </li>
              <li>
                <Link to="/portfolio" className="hover:text-[#4601FA] transition-colors">
                  Portfolio
                </Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-[#4601FA] transition-colors">
                  Contatti
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="hover:text-[#4601FA] transition-colors">
                  Privacy Policy
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 3: Servizi (3 cols) */}
          <div className="lg:col-span-3 space-y-4">
            <h3 className="text-xs uppercase tracking-wider font-extrabold text-[#4601FA]">Servizi & Soluzioni</h3>
            <ul className="space-y-2.5 text-sm text-gray-600">
              <li>
                <Link to="/services" className="flex items-center gap-2 hover:text-[#4601FA] transition-colors">
                  <Code className="w-3.5 h-3.5 text-[#4601FA]" />
                  <span>Sviluppo Software Custom</span>
                </Link>
              </li>
              <li>
                <Link to="/services" className="flex items-center gap-2 hover:text-[#4601FA] transition-colors">
                  <Layout className="w-3.5 h-3.5 text-[#4601FA]" />
                  <span>Landing Page & Siti Vetrina</span>
                </Link>
              </li>
              <li>
                <Link to="/services" className="flex items-center gap-2 hover:text-[#4601FA] transition-colors">
                  <Cpu className="w-3.5 h-3.5 text-[#4601FA]" />
                  <span>Integrazione AI & Automazioni</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 4: Contact CTA Box (3 cols) */}
          <div className="lg:col-span-3 bg-white border border-gray-200 rounded-2xl p-5 space-y-4 shadow-sm">
            <h4 className="text-base font-bold text-gray-900">
              Hai un'idea in mente?
            </h4>
            <p className="text-xs text-gray-600 leading-relaxed">
              Pianifichiamo una call conoscitiva per valutare fattibilità e le tempistiche.
            </p>
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-xl text-xs font-bold text-black bg-[#ADFE03] hover:bg-[#9ee802] border border-black/10 transition-all duration-200 shadow-sm hover:shadow group"
              >
                <span>Prenota una Consulenza</span>
                <ArrowUpRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </motion.div>
          </div>

        </div>

        {/* Footer Bottom Bar */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-gray-600">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 flex-wrap">
            <span>© {currentYear} {CONTACT_INFO.name}. Tutti i diritti riservati.</span>
            <span className="hidden sm:inline text-gray-300">|</span>
            <span>P. IVA: {CONTACT_INFO.pIva}</span>
            <span className="hidden sm:inline text-gray-300">|</span>
            <Link to="/privacy" className="hover:text-[#4601FA] hover:underline transition-colors">
              Informativa Privacy (GDPR)
            </Link>
          </div>
          <div className="flex items-center gap-1.5 font-medium text-gray-600">
            <span>Powered by</span>
            <span className="font-semibold text-black">{CONTACT_INFO.name}</span>
          </div>
        </div>

      </div>
    </footer>
  );
}
